import { Pause, Play, Mic, MicOff, ArrowRightLeft, PhoneOff, PhoneIncoming, Coffee } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useSoftphone } from '@/store/softphone';
import { useAgentState } from '@/lib/agentState';
import { Config } from '@/lib/api';
import clsx from 'clsx';

/**
 * Topbar'da tüm çağrı kontrolleri her zaman görünür — state'e göre disabled.
 * Buton stili: 40px dairesel, gradient + soft glow (hover'da büyür).
 */
export function TopbarSoftphoneActions() {
  const sp = useSoftphone();
  const { state, call, muted, onHold, toggleMute, toggleHold, hangup, accept, breakMode, queueAction } = sp;
  const agentState = useAgentState();
  const { data: pbxCfg } = useQuery({ queryKey: ['pbx-config'], queryFn: Config.pbx });

  const ringingInbound = state === 'ringing' && call?.direction === 'inbound';
  const inCall = state === 'in_call';
  const hangupEnabled = inCall || state === 'calling' || (state === 'ringing' && call?.direction === 'outbound');

  const canEnterBreak = (agentState === 'kuyrukta' || agentState === 'kuyruk-disi') && !queueAction;
  const canExitBreak = agentState === 'mola' && !queueAction;
  const breakEnabled = (canEnterBreak || canExitBreak) && state === 'registered' && !!pbxCfg;
  const breakTitle = breakMode ? 'Moladan dön' : 'Molaya gir';
  const onBreakClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!breakEnabled || !pbxCfg) return;
    if (breakMode) {
      // Molada → direkt resume, sebep sorma
      sp.triggerQueueAction('resume', pbxCfg.queue_codes.resume);
      return;
    }
    // Molaya giriyor → sebep popover'ını aç (sebep seçince trigger'lar)
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    sp.openBreakReasonPopover({
      top: rect.bottom + 8,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  };

  return (
    <div className="flex items-center gap-2 pr-2.5 mr-1.5 border-r border-slate-300/60">
      <CircleBtn title="Cevapla" variant="success" pulse={ringingInbound} disabled={!ringingInbound} onClick={accept}>
        <PhoneIncoming size={16} strokeWidth={2.25} />
      </CircleBtn>

      <div className="w-px h-7 bg-slate-300/60 mx-0.5" />

      <CircleBtn title="Beklet" disabled={!inCall} active={onHold} onClick={toggleHold}>
        {onHold ? <Play size={16} strokeWidth={2.25} /> : <Pause size={16} strokeWidth={2.25} />}
      </CircleBtn>
      <CircleBtn title="Sustur" disabled={!inCall} active={muted} onClick={toggleMute}>
        {muted ? <MicOff size={16} strokeWidth={2.25} /> : <Mic size={16} strokeWidth={2.25} />}
      </CircleBtn>
      <CircleBtn
        title="Aktar"
        disabled={!inCall}
        dataAttr="data-transfer-trigger"
        onClick={(e) => {
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          sp.openTransferSheet({
            top: rect.bottom + 8,
            right: Math.max(8, window.innerWidth - rect.right),
          });
        }}
      >
        <ArrowRightLeft size={16} strokeWidth={2.25} />
      </CircleBtn>
      {/* Tuş takımı (DTMF) butonu kaldırıldı — Aktar popover'ında zaten
          keypad var, ayrı DTMF sheet'i call center agent senaryosunda
          nadir kullanılıyor. */}
      <CircleBtn title="Çağrıyı sonlandır" variant="danger" disabled={!hangupEnabled} onClick={hangup}>
        <PhoneOff size={16} strokeWidth={2.25} />
      </CircleBtn>

      <div className="w-px h-7 bg-slate-300/60 mx-0.5" />

      <CircleBtn
        title={breakTitle}
        variant={breakMode ? 'success' : undefined}
        active={breakMode}
        disabled={!breakEnabled}
        dataAttr="data-break-trigger"
        onClick={onBreakClick}
      >
        <Coffee size={16} strokeWidth={2.25} />
      </CircleBtn>
    </div>
  );
}

function CircleBtn({ title, onClick, disabled, active, variant, pulse, children, dataAttr }: {
  title: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  active?: boolean;
  variant?: 'danger' | 'success';
  pulse?: boolean;
  children: React.ReactNode;
  /** Click-outside detection için popover trigger data-attribute'u
   *  (örn 'data-transfer-trigger'). Set edilirse buton'a kuru attribute olarak
   *  eklenir, ilgili popover hook'u trigger'ı bu sayede tanır. */
  dataAttr?: string;
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      {...(dataAttr ? { [dataAttr]: true } : {})}
      className={clsx(
        'relative w-10 h-10 rounded-full flex items-center justify-center border transition-all duration-150',
        'hover:-translate-y-0.5 active:translate-y-0 active:scale-95',
        'disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none',
        variant === 'danger'
          ? 'bg-gradient-to-br from-red-400 to-red-600 hover:from-red-500 hover:to-red-700 text-white border-red-600/30 shadow-[0_4px_12px_-2px_rgba(239,68,68,0.5)] hover:shadow-[0_6px_18px_-3px_rgba(239,68,68,0.6)] disabled:bg-none disabled:bg-surface disabled:text-slate-400 disabled:border-slate-200 disabled:shadow-none'
          : variant === 'success'
            ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 hover:from-emerald-500 hover:to-emerald-700 text-[#08140d] border-emerald-600/30 shadow-[0_4px_12px_-2px_rgba(16,185,129,0.5)] hover:shadow-[0_6px_18px_-3px_rgba(16,185,129,0.6)] disabled:bg-none disabled:bg-surface disabled:text-slate-400 disabled:border-slate-200 disabled:shadow-none'
            : active
              ? 'bg-gradient-to-br from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-[#08140d] border-amber-500/40 shadow-[0_3px_10px_-2px_rgba(245,158,11,0.5)]'
              : 'bg-surface hover:bg-slate-50 text-slate-600 hover:text-slate-900 border-slate-200 shadow-sm hover:shadow-md',
      )}
    >
      {/* Cevapla butonunda ringing iken pulse halkası */}
      {pulse && !disabled && (
        <span className="absolute inset-0 rounded-full ring-2 ring-emerald-400/70 animate-ping pointer-events-none" />
      )}
      <span className="relative z-10">{children}</span>
    </button>
  );
}
